import type { TelemetryConsentStore } from '@/application/telemetry/consentStore'
import {
  closeObservability,
  initObservability,
} from '@/infrastructure/sentry/sentryClient'

/**
 * Drives Sentry's SDK lifecycle from the consent store (ADR-035, S35).
 *
 * Only acts on transitions — `false → true` runs `initObservability(true)`,
 * `true → false` runs `closeObservability()`. A notify that keeps the
 * value unchanged is ignored. The initial state matches the store's
 * default (`false`), so a fresh user with no persisted consent never
 * triggers init.
 *
 * Both side effects swallow errors with a console log — observability
 * is a developer concern, not a user-facing failure path.
 */

export interface ObservabilityDeps {
  init: (consent: boolean) => Promise<boolean>
  close: () => Promise<void>
  logError: (message: string, error: unknown) => void
}

export const defaultObservabilityDeps: ObservabilityDeps = {
  init: initObservability,
  close: closeObservability,
  logError: (message, error) => {
    console.error(message, error)
  },
}

export function subscribeObservabilityToConsent(
  store: TelemetryConsentStore,
  deps: ObservabilityDeps = defaultObservabilityDeps,
): () => void {
  let lastSeen = false

  const onChange = (): void => {
    const current = store.isConsenting()
    if (current === lastSeen) return
    lastSeen = current
    if (current) {
      void deps.init(true).catch((err) => {
        deps.logError('observability init failed', err)
      })
    } else {
      void deps.close().catch((err) => {
        deps.logError('observability close failed', err)
      })
    }
  }

  return store.subscribe(onChange)
}
